/* eslint-disable camelcase */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import { pluck } from "underscore";
import { Option } from "../approversTab";

type RulesGroup = {
  _type: string;
  attr: string;
  value: string;
  _destroy: string;
  id: string;
};

type RulesGroups = {
  conditions_attributes: Array<RulesGroup>;
  _type: string;
};

type DestroyAward = {
  destroy: boolean;
};

type AllowedApproverIDs = string[];

type ApprovalProcess = {
  levels: Array<AllowedApproverIDs>;
};

type ImageBlob = {
  name?: string;
  size?: string;
  lastModified?: string;
  data?: string;
  type?: string;
};

type Award = {
  approval_process?: null | DestroyAward | ApprovalProcess;
  budget_boost?: string;
  budget_period_type?: string;
  link_text?: string;
  link_url?: string;
  first_name: string;
  amount?: string;
  reason?: string;
  claimable?: boolean;
  require_proof?: boolean;
  user: {
    avatar?: ImageBlob;
    remove_avatar?: boolean;
  };
  emoji_avatar?: string;
};

type Params = {
  autoapprove: boolean;
  award: Award;
  rules_group: RulesGroups;
};

export const getRulesGroupConditions = (formState): Array<RulesGroup> => {
  const rulesGroupConditionsAttrs = Object.keys(formState).filter((key) => {
    return (
      key.trim().includes("rules_group[conditions_attributes]") &&
      key.includes("[attr]")
    );
  });

  const rulesGroupConditionsValues = Object.keys(formState).filter((key) => {
    return (
      key.trim().includes("rules_group[conditions_attributes]") &&
      key.includes("[value]")
    );
  });

  const rulesGroupConditionsTypes = Object.keys(formState).filter((key) => {
    return (
      key.trim().includes("rules_group[conditions_attributes]") &&
      key.includes("[_type]")
    );
  });
  const rulesGroupConditionsDestroy = Object.keys(formState).filter((key) => {
    return (
      key.trim().includes("rules_group[conditions_attributes]") &&
      key.includes("[_destroy]")
    );
  });
  const rulesGroupConditionsIDs = Object.keys(formState).filter((key) => {
    return (
      key.trim().includes("rules_group[conditions_attributes]") &&
      key.includes("[id]")
    );
  });

  const rules_group_conditions_attributes = [];

  rulesGroupConditionsAttrs.forEach((_, index) => {
    rules_group_conditions_attributes.push({
      _type: formState[rulesGroupConditionsTypes[index]],
      attr: formState[rulesGroupConditionsAttrs[index]],
      value: formState[rulesGroupConditionsValues[index]],
      _destroy: formState[rulesGroupConditionsDestroy[index]],
      id: formState[rulesGroupConditionsIDs[index]],
    });
  });

  return rules_group_conditions_attributes;
};

export const getSelectedOptionIDs = (selectedOptions): Option[] => {
  const levelApprovers = selectedOptions.map((level) => {
    const validApprovers = level.filter((approver) => {
      return approver.active;
    });
    return pluck(validApprovers, "value");
  });
  const levels = levelApprovers.map((ids, index) => {
    return { position: index + 1, allowed_approver_ids: ids };
  });
  return levels;
};

export const blobToData = (blob: Blob): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      return resolve(reader.result as string);
    };
    reader.readAsDataURL(blob);
  });
};

const getImageBlob = async (fileList): Promise<ImageBlob> => {
  if (fileList?.length) {
    const imageBinaryData = await blobToData(fileList[0]);

    return {
      name: fileList[0].name,
      size: fileList[0].size,
      lastModified: fileList[0].lastModified,
      data: imageBinaryData.replace(/^data:image\/[a-z]+;base64,/, ""),
      type: fileList[0].type,
    };
  }
  return {};
};

export const handleGetFormStateData = async (
  formState,
  approvedAutomatically,
  resetPendingRequests,
  selectedOptions,
  isEditing,
  isClaimable,
  approvalProcessID
): Promise<Params> => {
  const data = {
    autoapprove: approvedAutomatically,
    award: {
      // THESE ARE ALL HARDCODED IN TWO DAMN PLACES!
      // See: _award_form_standard.html.haml _claimable_award_form.html.haml
      // Note: user is nested with the name field, but rules_group IS NOT
      first_name: formState["award[first_name]"],
      budget_boost: formState["award[budget_boost]"],
      budget_period_type: formState["award[budget_period_type]"],
      amount: formState["award[amount]"],
      reason: formState["award[reason]"],
      claimable: formState["award[claimable]"],
      description: formState["award[description]"],
      link_text: formState["award[link_text]"],
      link_url: formState["award[link_url]"],
      quantity: formState["award[quantity]"],
      claim_limit: formState["award[claim_limit]"],
      claim_limit_period: formState["award[claim_limit_period]"],
      require_proof: formState["require_proof"],
      bonus_privacy: formState["award[bonus_privacy]"],
      bonus_privacy_changeable: formState["bonus_privacy_changeable"],
      user: {
        avatar: await getImageBlob(formState["award[user][avatar]"]),
        remove_avatar: formState["award[user][remove_avatar]"],
      },
      emoji_avatar: formState["award[emoji_avatar]"],
      autoapprove: approvedAutomatically,
    },
    reset_levels_of_pending_requests: resetPendingRequests,
    rules_group: {
      _type: formState["rules_group[_type]"],
      conditions_attributes: getRulesGroupConditions(formState),
    },
  };

  const selectedOptionIDs = getSelectedOptionIDs(selectedOptions);
  let approvalProc = null;
  if (isEditing && approvedAutomatically && approvalProcessID) {
    approvalProc = {
      destroy: true,
    };
  } else if (!approvedAutomatically) {
    approvalProc = {
      id: approvalProcessID,
      levels: selectedOptionIDs,
    };
  }

  if (approvalProc) {
    data.award["approval_process"] = approvalProc;
  }

  return data;
};

export const getAwardApproversOptions = (awardApprovers): Array<Option> => {
  return awardApprovers.map(([id, email, inactive]) => {
    let active = !inactive;
    return { value: id, label: email, active };
  });
};

const levelValidation = ({ name, value }): string => {
  if (!value || value.length === 0) {
    return `${name.charAt(0).toUpperCase()}${name
      .slice(1)
      .split("_")
      .join(" ")} cannot be empty`;
  }
  return null;
};

const levelsValidation = (levelErrors): boolean => {
  if (
    Object.values(levelErrors).some((value: string[]) => {
      return value.length;
    })
  ) {
    return true;
  }
  return false;
};

const levelApproversValidation = (levelState): boolean => {
  return levelState?.levels?.some((level) => {
    return level?.every((approver) => {
      return !approver.active;
    });
  });
};

export const validate = {
  level: (level): string => {
    return levelValidation(level);
  },
  levels: (levelErrorState): boolean => {
    return levelsValidation(levelErrorState);
  },
  levels_approvers: (levelState): boolean => {
    return levelApproversValidation(levelState);
  },
};
